import ScButton from "@/src/components/ui-kit/button";
import TimeSpinner, { TimePeriod } from "@/src/components/ui-kit/time-spinner";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";
import { Calendar, DateData } from "react-native-calendars";

type PeriodMarking = Readonly<{
  startingDay?: boolean;
  endingDay?: boolean;
  color?: string;
  textColor?: string;
}>;

export type DateTimeResult = {
  startDate: string | null;
  endDate: string | null;
  startTime: string;
  endTime: string;
  startPeriod: TimePeriod | null;
  endPeriod: TimePeriod | null;
};

type DateTimePickerModalProps = Readonly<{
  visible: boolean;
  variant?: "date-only" | "date-time";
  singleDay?: boolean;
  initialValues?: Partial<DateTimeResult>;
  onClose: () => void;
  onApply: (result: DateTimeResult) => void;
}>;

const getDatesInRange = (
  start: string,
  end: string,
): Record<string, PeriodMarking> => {
  const dates: Record<string, PeriodMarking> = {};
  const current = new Date(start);
  const last = new Date(end);
  while (current <= last) {
    const dateString = current.toISOString().split("T")[0];
    dates[dateString] = { color: "#0669b3", textColor: "white" };
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const padTwoDigits = (n: number) => String(n).padStart(2, "0");

const to24h = (hour: number, minute: number, period: TimePeriod) => {
  let h = hour % 12;
  if (period === "PM") h += 12;
  return `${padTwoDigits(h)}:${padTwoDigits(minute)}`;
};

const DateTimePickerModal = ({
  visible,
  variant = "date-time",
  singleDay = false,
  initialValues,
  onClose,
  onApply,
}: DateTimePickerModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const showTime = variant === "date-time";

  const [startDate, setStartDate] = useState<string | null>(
    initialValues?.startDate ?? null,
  );
  const [endDate, setEndDate] = useState<string | null>(
    initialValues?.endDate ?? null,
  );

  const [startHour, setStartHour] = useState(
    initialValues?.startTime
      ? parseInt(initialValues.startTime.split(":")[0]) % 12 || 12
      : 7,
  );
  const [startMinute, setStartMinute] = useState(
    initialValues?.startTime
      ? parseInt(initialValues.startTime.split(":")[1])
      : 0,
  );
  const [startPeriod, setStartPeriod] = useState<TimePeriod>(
    initialValues?.startPeriod ?? "AM",
  );
  const [endHour, setEndHour] = useState(
    initialValues?.endTime
      ? parseInt(initialValues.endTime.split(":")[0]) % 12 || 12
      : 9,
  );
  const [endMinute, setEndMinute] = useState(
    initialValues?.endTime ? parseInt(initialValues.endTime.split(":")[1]) : 0,
  );
  const [endPeriod, setEndPeriod] = useState<TimePeriod>(
    initialValues?.endPeriod ?? "PM",
  );

  const onDayPress = (day: DateData) => {
    if (singleDay) {
      setStartDate(day.dateString);
      setEndDate(day.dateString);
      return;
    }
    if (!startDate || (startDate && endDate)) {
      setStartDate(day.dateString);
      setEndDate(null);
    } else {
      if (day.dateString < startDate) {
        setEndDate(startDate);
        setStartDate(day.dateString);
      } else {
        setEndDate(day.dateString);
      }
    }
  };

  const markedDates = useMemo(() => {
    if (!startDate) return {};
    if (singleDay || !endDate) {
      return {
        [startDate]: {
          startingDay: true,
          endingDay: true,
          color: "#0669b3",
          textColor: "white",
        },
      };
    }
    const range = getDatesInRange(startDate, endDate);
    return {
      ...range,
      [startDate]: { ...range[startDate], startingDay: true },
      [endDate]: { ...range[endDate], endingDay: true },
    };
  }, [startDate, endDate, singleDay]);

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    onApply({
      startDate: null,
      endDate: null,
      startTime: "",
      endTime: "",
      startPeriod: null,
      endPeriod: null,
    });
  };

  const handleCancel = () => {
    handleClear();
    onClose();
  };

  const canApply = singleDay ? !!startDate : !!(startDate && endDate);

  const handleApply = () => {
    if (!canApply || !startDate) return;
    const finalEndDate = singleDay ? startDate : (endDate ?? startDate);
    onApply({
      startDate,
      endDate: finalEndDate,
      startTime: showTime
        ? new Date(
            `${startDate}T${to24h(startHour, startMinute, startPeriod)}:00`,
          ).toISOString()
        : "",
      endTime: showTime
        ? new Date(
            `${finalEndDate}T${to24h(endHour, endMinute, endPeriod)}:00`,
          ).toISOString()
        : "",
      startPeriod,
      endPeriod,
    });
    onClose();
  };
  return (
    <Modal
      transparent
      visible={visible}
      onDismiss={onClose}
      animationType="fade"
    >
      <Pressable
        style={varStyle}
        className="justify-center items-center bg-shadow-dark flex-1 px-6"
        onPress={onClose}
      >
        <Pressable
          className="bg-ash rounded-2xl w-full max-w-md"
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View className="flex-row justify-between items-center border-b border-ash-3 p-4">
            <Text className="text-sx text-onyx">{t("LABEL_SELECT_DATE")}</Text>
            <View className="flex-row gap-3">
              <Pressable
                onPress={handleClear}
                className="flex-row items-center gap-1 hover:bg-ash-3 hover:rounded-lg"
              >
                <CloseCircle
                  size={18}
                  color={themeVars["--color-crimson-default"]}
                />
                <Text className="text-crimson font-medium">
                  {t("BTN_CLEAR")}
                </Text>
              </Pressable>
              <Pressable onPress={onClose} className="rounded-full hover:bg-ash-3">
                <CloseCircle color={themeVars["--color-primary-default"]} />
              </Pressable>
            </View>
          </View>

          {/* Calendar */}
          <View className="px-1">
            <Calendar
              markingType="period"
              markedDates={markedDates}
              onDayPress={onDayPress}
              theme={{
                calendarBackground: themeVars["--color-ash-default"],
                backgroundColor: themeVars["--color-ash-default"],

                textSectionTitleColor: themeVars["--color-slate-default"],
                dayTextColor: themeVars["--color-onyx-default"],
                todayTextColor: themeVars["--color-primary-default"],

                monthTextColor: themeVars["--color-onyx-default"],
                arrowColor: themeVars["--color-primary-default"],

                textDisabledColor: themeVars["--color-ash-1"],

                selectedDayBackgroundColor:
                  themeVars["--color-primary-default"],
                selectedDayTextColor: themeVars["--color-chalk-default"],

                dotColor: themeVars["--color-primary-default"],
                selectedDotColor: themeVars["--color-chalk-default"],

                indicatorColor: themeVars["--color-primary-default"],
              }}
            />
          </View>

          {/* Time section */}
          {showTime && (
            <>
              <View className="border-t border-ash-3 mx-4 mt-2 mb-3" />
              <View className="px-4 pb-2">
                <Text className="text-sm font-semibold text-onyx mb-3">
                  {t("LABEL_SELECT_TIME")}
                </Text>
                <View className="flex-row items-center justify-around">
                  <TimeSpinner
                    label={t("LABEL_START")}
                    hour={startHour}
                    minute={startMinute}
                    period={startPeriod}
                    onHourChange={setStartHour}
                    onMinuteChange={setStartMinute}
                    onPeriodChange={setStartPeriod}
                  />
                  <Text className="text-slate text-lg mt-4">→</Text>
                  <TimeSpinner
                    label={t("LABEL_END")}
                    hour={endHour}
                    minute={endMinute}
                    period={endPeriod}
                    onHourChange={setEndHour}
                    onMinuteChange={setEndMinute}
                    onPeriodChange={setEndPeriod}
                  />
                </View>
              </View>
            </>
          )}

          <View className="flex-row justify-end gap-3 mt-2 px-4 py-4 border-t border-ash-3">
            <ScButton
              text={t("BTN_CANCEL")}
              mode="outlined"
              onPress={handleCancel}
            />
            <ScButton
              text={showTime ? t("BTN_DONE") : t("BTN_APPLY")}
              disabled={!canApply}
              onPress={handleApply}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};
export default DateTimePickerModal;
